import { Button, ButtonProps, Typography } from "@timeedit/ui-components";
import { ConfigProvider } from "@timeedit/ui-components/antd";
import cn from "classnames";
import { ReactNode, useId } from "react";
import {
  colorInfo,
  colorInfoActive,
  colorInfoHover,
} from "@timeedit/ui-components/theme/variables";
import "./buttonWithDescription.scss";

const waitlistButtonTheme = {
  components: {
    Button: {
      defaultColor: colorInfo,
      defaultBorderColor: colorInfo,
      defaultHoverColor: colorInfoHover,
      defaultHoverBorderColor: colorInfoHover,
      defaultActiveColor: colorInfoActive,
      defaultActiveBorderColor: colorInfoActive,
    },
  },
};

/**
 * Hidden accessible name for the button: `label` is read first, then the
 * elements in `labelledBy`, with `hint` appended unless the button is disabled.
 * Loading keeps the hint so the name stays stable while a request is in flight.
 */
export type AccessibleLabel = {
  /** Read before the referenced elements, e.g. "Register to". */
  label: string;
  /** Appended to the label unless the button is disabled. */
  hint?: string;
  /** Space-separated ids, e.g. from `groupControlsLabelledBy(groups)`. */
  labelledBy: string;
};

interface ButtonWithDescriptionProps
  extends Omit<ButtonProps, "aria-labelledby"> {
  isWaitlist?: boolean;
  description?: ReactNode;
  accessibleLabel?: AccessibleLabel;
  /** Ids of visible notifications, read after the description. Falsy ids are skipped. */
  notificationIds?: Array<string | false>;
}

export function ButtonWithDescription({
  accessibleLabel,
  notificationIds = [],
  description,
  isWaitlist = false,
  disabled,
  loading,
  onClick,
  ...buttonProps
}: ButtonWithDescriptionProps): React.JSX.Element {
  const descriptionId = useId();
  const labelId = useId();
  const labelledBy = accessibleLabel
    ? `${labelId} ${accessibleLabel.labelledBy}`
    : undefined;
  // Disabled buttons stay focusable so screen reader users can find them and
  // hear why they are disabled.
  const isDisabled = Boolean(disabled || loading);
  const hasDescription = Boolean(description);
  const describedBy =
    [hasDescription ? descriptionId : undefined, ...notificationIds]
      .filter(Boolean)
      .join(" ") || undefined;
  return (
    <>
      <div>
        <ConfigProvider theme={isWaitlist ? waitlistButtonTheme : undefined}>
          <Button
            {...buttonProps}
            loading={loading}
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            aria-disabled={isDisabled || undefined}
            onClick={isDisabled ? undefined : onClick}
            className={cn("tw:w-[112px] tw:p-0", buttonProps.className, {
              "button-with-description--disabled": isDisabled,
              "ant-btn-disabled": disabled,
            })}
          >
            {buttonProps.children}
          </Button>
        </ConfigProvider>
      </div>

      <Typography.Text id={hasDescription ? descriptionId : undefined}>
        {description}
      </Typography.Text>
      {accessibleLabel && (
        <div
          className="button-with-description__label"
          id={labelId}
          aria-hidden
        >
          {accessibleLabel.hint && !disabled
            ? `${accessibleLabel.label}. ${accessibleLabel.hint}`
            : accessibleLabel.label}
        </div>
      )}
    </>
  );
}
